/**
 * El autoguardado del editor.
 *
 * Cuatro reglas, y ninguna más. El primer diseño de esto tenía un almacén global, una máquina de
 * siete estados repartida en tres capas, un canal entre pestañas, un borrador local y guardado al
 * ocultarse la página; **siete de sus once fallos eran interacciones entre esos mecanismos**, y
 * el peor perdía datos. Lo que queda:
 *
 * 1. **Instantánea de lo enviado.** Al confirmarse se descuenta solo eso, nunca el buffer entero.
 *    Es lo que hace que teclear mientras la petición viaja no pierda nada.
 * 2. **Un envío en vuelo por fila.** Lo nuevo se acumula y se programa otro al volver.
 * 3. **Control optimista con la versión**, que se actualiza con la que devuelve el servidor.
 * 4. **Cuatro estados** y un reintento que conserva los cambios.
 */

import { saveWithVersion, type SaveOutcome } from './api.js';

export type SaveState = 'saved' | 'pending' | 'saving' | 'failed' | 'conflict';

export type SaveTable = 'subjects' | 'quizzes' | 'questions' | 'options';

/** Cuánto se espera tras la última pulsación antes de guardar (§9.1). */
export const DEBOUNCE_MS = 800;

/** Esperas del reintento. Tras la última, se deja de intentar y el estado queda en fallido. */
const RETRY_DELAYS_MS = [1_000, 3_000, 8_000];

export interface RowSaverOptions<T> {
  readonly table: SaveTable;
  readonly id: string;
  readonly initialVersion: number;
  readonly onState: (state: SaveState) => void;
  readonly onSaved: (row: T) => void;
  /** Inyectables para que las pruebas no dependan del reloj. */
  readonly save?: typeof saveWithVersion;
  readonly debounceMs?: number;
}

interface VersionedRow {
  readonly version: number;
}

/**
 * Guarda los cambios de **una fila**.
 *
 * Se crea uno por fila que se edita: una pregunta, una opción, el cuestionario. Así el conflicto
 * se detecta con la granularidad con la que de verdad ocurre.
 */
export class RowSaver<T extends VersionedRow> {
  /** Lo que está pendiente de enviar. Nunca se vacía entero: se descuenta lo confirmado. */
  private buffer: Record<string, unknown> = {};
  /** Lo que viaja ahora mismo. Es la instantánea que evita la pérdida de datos. */
  private inFlight: Record<string, unknown> | null = null;
  private version: number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private retry = 0;
  private disposed = false;
  /** Se ha pedido guardar ya y había un envío en vuelo: al volver, sale otro sin esperar. */
  private pendienteDeCierre = false;
  /** La fila ha cambiado por debajo. No se reintenta: reintentar sobrescribiría lo ajeno. */
  private enConflicto = false;

  private readonly save: typeof saveWithVersion;
  private readonly debounceMs: number;

  constructor(private readonly options: RowSaverOptions<T>) {
    this.version = options.initialVersion;
    this.save = options.save ?? saveWithVersion;
    this.debounceMs = options.debounceMs ?? DEBOUNCE_MS;
  }

  /** Registra un cambio. No guarda todavía: espera a que haya una pausa. */
  change(patch: Record<string, unknown>): void {
    if (this.disposed) return;

    this.buffer = { ...this.buffer, ...patch };
    this.emitir('pending');
    this.schedule();
  }

  /**
   * Guarda ya, sin esperar la pausa.
   *
   * Si hay una petición viajando, `send()` no puede sacar otra —solo hay un envío en vuelo por
   * fila—, así que se deja constancia de que hay prisa y el manejador de la respuesta saca el
   * siguiente en cuanto vuelve. Sin esto se perdía **todo lo tecleado durante el viaje de una
   * petición**: `flush()` salía sin enviar, `dispose()` marcaba la clase como suelta, y la
   * respuesta se encontraba con que ya no había a quien avisar y no reprogramaba nada.
   */
  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.inFlight !== null) {
      this.pendienteDeCierre = true;
      return;
    }
    void this.send();
  }

  /**
   * Deja de guardar.
   *
   * Lo que ya se pidió guardar SE GUARDA: `disposed` significa «esta pantalla ya no existe, no le
   * hables», no «tira lo que la profesora acaba de escribir». Lo que se apaga son los avisos de
   * estado y los reintentos, que no tendrían a quién informar.
   */
  dispose(): void {
    this.disposed = true;
    if (this.timer !== null) clearTimeout(this.timer);
  }

  /** Avisa del estado solo si queda alguien escuchando. */
  private emitir(state: SaveState): void {
    if (!this.disposed) this.options.onState(state);
  }

  /** Hay algo sin guardar ahora mismo. */
  get hasPendingChanges(): boolean {
    return Object.keys(this.buffer).length > 0 || this.inFlight !== null;
  }

  private schedule(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.send();
    }, this.debounceMs);
  }

  private async send(): Promise<void> {
    // Un envío en vuelo por fila: si ya hay uno, lo nuevo espera en el buffer y saldrá después.
    if (this.inFlight !== null) return;
    if (Object.keys(this.buffer).length === 0) return;
    // Tras un conflicto no se vuelve a intentar por su cuenta, ni siquiera al cerrar: quien
    // decide qué hacer con el trabajo ajeno es la persona, no esta clase.
    if (this.enConflicto) return;

    // LA INSTANTÁNEA. Lo que se envía queda congelado aquí; el buffer se vacía de lo enviado y
    // sigue recogiendo lo que el profesor teclee mientras la petición viaja.
    const snapshot = this.buffer;
    this.buffer = {};
    this.inFlight = snapshot;
    this.emitir('saving');

    const outcome: SaveOutcome<T> = await this.save<T>(
      this.options.table,
      this.options.id,
      this.version,
      snapshot,
    );

    this.inFlight = null;

    if (outcome.ok) {
      this.retry = 0;
      this.version = outcome.row.version;
      if (!this.disposed) this.options.onSaved(outcome.row);

      // Si el profesor siguió escribiendo, sale otro envío. Si no, ya está todo guardado.
      if (Object.keys(this.buffer).length > 0) {
        // Con la pantalla ya cerrada no se espera la pausa: sale inmediatamente, porque nadie va
        // a escribir más y lo único que queda es que llegue.
        if (this.disposed || this.pendienteDeCierre) {
          this.pendienteDeCierre = false;
          void this.send();
        } else {
          this.emitir('pending');
          this.schedule();
        }
      } else {
        this.pendienteDeCierre = false;
        this.emitir('saved');
      }
      return;
    }

    // El fallo NO pierde nada: lo enviado vuelve al buffer, por debajo de lo que llegara después,
    // que es más reciente y debe ganar.
    this.buffer = { ...snapshot, ...this.buffer };

    if (outcome.reason === 'conflict') {
      // Alguien cambió la fila. No se adivina quién ni se reintenta: reintentar sobrescribiría
      // su trabajo, que es lo contrario de lo que el conflicto pide.
      this.enConflicto = true;
      this.pendienteDeCierre = false;
      this.emitir('conflict');
      return;
    }

    // Con la pantalla cerrada no se reintenta: no habría a quién contarle que sigue fallando, y
    // un temporizador vivo sobre una pantalla que ya no existe no arregla nada.
    if (this.disposed) return;

    const delay = RETRY_DELAYS_MS[this.retry];
    if (delay === undefined) {
      this.emitir('failed');
      return;
    }

    this.retry += 1;
    this.emitir('failed');
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.send();
    }, delay);
  }
}
